/**
 * The single source of truth for numeric text entry in BrewBase.
 *
 * Three pure functions, no React / DOM. The numeric input components
 * (NumericInput, UnitInput, the NumericEditor cell, TimeInput's EFT fields)
 * all consume these — they are the standard, the components are consumers.
 *
 * Rules (locked):
 *  - '.' is THE decimal separator. ',' is ALWAYS decimal too, normalized to
 *    '.', NEVER thousands grouping. So "1,000" parses as 1.0, not 1000.
 *  - Disallowed characters are dropped, never cause a reset to "0".
 *  - Blank stays blank → genuine null, never 0.
 *  - Lone "-", lone ".", and trailing "1." are preserved mid-edit.
 *  - Spaces are tolerated while typing and stripped on emit/blur
 *    ("1 2 3" → 123).
 *  - Scientific notation and grouped-number paste ("1,000.00") are
 *    unsupported by design (comma is decimal): the 'e' / extra separators
 *    are stripped.
 */
/**
 * Keystroke-level filter. Returns the display text the field should show.
 * Only ever removes characters (output is a subsequence of the
 * comma-normalized input), which keeps caret restoration in the component
 * a simple length delta. Stateless.
 *
 * Keeps digits and spaces verbatim; normalizes ',' → '.'; keeps '-' only as
 * the first non-space character; keeps only the first '.'. Does not parse,
 * does not collapse leading zeros, does not drop a trailing '.'.
 */
export function sanitizeNumericText(raw) {
    // ',' is always the decimal separator.
    const normalized = raw.replace(/,/g, ".");
    // Keep digits, dot, minus, space; drop everything else (letters, 'e', etc).
    const filtered = normalized.replace(/[^0-9.\- ]/g, "");
    let result = "";
    for (const ch of filtered) {
        if (ch === "-") {
            // A minus survives only as the first non-space character.
            if (!result.includes("-") && !/[0-9.]/.test(result))
                result += ch;
        }
        else if (ch === ".") {
            // Only the first decimal separator survives.
            if (!result.includes("."))
                result += ch;
        }
        else {
            // Digit or space.
            result += ch;
        }
    }
    return result;
}
/**
 * The number path (default NumericInput mode, post-conversion UnitInput,
 * the cell editor, EFT day/hour). Returns null for blank / sign-only input.
 * Uses Number (not parseFloat) so partial junk like "12abc" yields null
 * rather than silently truncating to 12. Maps -0 to 0.
 */
export function parseDecimal(text) {
    const s = text.replace(/\s/g, "");
    if (s === "" || s === "-" || s === "." || s === "-.")
        return null;
    const n = Number(s);
    if (!Number.isFinite(n))
        return null;
    return n === 0 ? 0 : n;
}
/**
 * Drop the fractional part of numeric display text (keystroke-level integer
 * enforcement). "1.5" → "1", "-12.0" → "-12", "." → "". A pure prefix cut, so
 * the component's length-delta caret restoration still holds.
 */
export function stripFractional(text) {
    const dot = text.indexOf(".");
    return dot === -1 ? text : text.slice(0, dot);
}
/**
 * Count the fractional digits in a finite number's canonical string form.
 * `decimalsOf(0.001)` → 3, `decimalsOf(5)` → 0. Used to round snapped values
 * back to a clean scale and avoid binary-float dust (0.1 + 0.2 = 0.30000…4).
 */
function decimalsOf(n) {
    const s = String(n);
    if (s.includes("e") || s.includes("E"))
        return 0;
    const dot = s.indexOf(".");
    return dot === -1 ? 0 : s.length - dot - 1;
}
/**
 * Apply validation rules to a numeric value: snap to the nearest `step`
 * (anchored at `min ?? 0`, per the HTML number-input model), then clamp into
 * `[min, max]`. Pure and float-safe — the result is rounded to the combined
 * scale of `step` and the snap anchor so `0.1`-style steps don't drift.
 *
 * This is a COMMIT-time rule (NumericInput applies it on blur, never per
 * keystroke), so mid-edit fragments like "1" while typing "12" are untouched.
 * Omitted bounds are no-ops, so `applyNumericBounds(n, {})` returns `n`.
 */
export function applyNumericBounds(value, bounds) {
    const { min, max, step } = bounds;
    let v = value;
    if (step != null && step > 0) {
        const base = min ?? 0;
        const snapped = base + Math.round((v - base) / step) * step;
        const decimals = Math.max(decimalsOf(step), decimalsOf(base));
        v = decimals > 0 ? Number(snapped.toFixed(decimals)) : Math.round(snapped);
    }
    if (min != null && v < min)
        v = min;
    if (max != null && v > max)
        v = max;
    return v;
}
/**
 * The lossless string path (precise mode, for pg NUMERIC `number_value`
 * columns surfaced as string). Preserves the user's stated scale —
 * trailing fractional zeros are kept ("1.500" → "1.500"). Drops a lone
 * trailing dot ("1." → "1"), collapses leading zeros keeping one
 * ("007" → "7"), and collapses an all-zero magnitude to positive zero
 * ("-0" → "0"). Returns null for blank / sign-only / invalid input.
 */
export function normalizeForEmit(text) {
    const s = text.replace(/\s/g, "");
    if (s === "" || s === "-" || s === "." || s === "-.")
        return null;
    if (!/^-?\d*\.?\d*$/.test(s) || !/\d/.test(s))
        return null;
    let negative = s.startsWith("-");
    const body = negative ? s.slice(1) : s;
    const dot = body.indexOf(".");
    let intPart = dot === -1 ? body : body.slice(0, dot);
    const fracPart = dot === -1 ? "" : body.slice(dot + 1);
    // Collapse leading zeros, keep at least one integer digit.
    intPart = intPart.replace(/^0+/, "");
    if (intPart === "")
        intPart = "0";
    // Reassemble; an empty fractional part drops the trailing dot.
    const out = fracPart.length > 0 ? `${intPart}.${fracPart}` : intPart;
    // An all-zero magnitude is positive zero, never "-0".
    if (negative && /^0(\.0*)?$/.test(out))
        negative = false;
    return negative ? `-${out}` : out;
}
//# sourceMappingURL=numeric-text.js.map